import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import {
  CurrencyValue,
  BarChart,
  formatDatetime,
  formatNumber
} from '@blockanalitica/ui';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useChartPeriod } from '@/hooks';

export default function HistoricGraphs({
  data,
  type,
  timeFilter,
  chartDaysAgo
}: {
  data: SimpleChartData[];
  type: string;
  timeFilter: JSX.Element;
  chartDaysAgo: string;
}) {
  const theme = useTheme();
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const datasets = useMemo(() => {
    let datasets: SimpleChartDataset[];
    if (type === 'deposit_withdraw') {
      datasets = [
        {
          xField: 'date',
          yField: 'deposit_volume',
          label: 'Deposit',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'withdraw_volume',
          label: 'Withdraw',
          backgroundColor: theme.colors.chartDown
        }
      ];
    } else if (type === 'borrow_repay') {
      datasets = [
        {
          xField: 'date',
          yField: 'borrow_volume',
          label: 'Borrow',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'repay_volume',
          label: 'Repay',
          backgroundColor: theme.colors.chartDown
        }
      ];
    }

    return datasets;
  }, [theme.colors, type]);

  const valueFormatter = useCallback((value: number) => {
    {
      return <CurrencyValue value={value} options={{ currency: 'USD' }} />;
    }
  }, []);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  const yScaleValueFormatter = useCallback(
    (value: number) => formatNumber(value),
    []
  );

  return (
    <BarChart
      key={type}
      data={data}
      datasets={datasets}
      valueFormatter={valueFormatter}
      timeFilter={timeFilter}
      config={config}
      subValueFormatter={subValueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
    />
  );
}
